// Workflow Mode — hierarchical animated agent graph (Factorio/RPG style).
// - Orchestrator-Prime at top, specialists arranged below.
// - Edges animated with flowing tokens, latency labels, payload labels.
// - Each node clickable -> AgentInspectorDrawer.
// - Pan + zoom + minimap + reset view.
// - "Executar" triggers a real animated flow + calls execute-workflow edge.
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
  MarkerType,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNeural, type Agent } from "@/store/neuralStore";
import { Button } from "@/components/ui/button";
import { Play, Save, Loader2, FileText, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AgentInspectorDrawer } from "../AgentInspectorDrawer";
import { cn } from "@/lib/utils";

interface AgentNodeData {
  agent: Agent;
  active: boolean;
  progress: number; // 0-100
  status: "idle" | "thinking" | "executing" | "done" | "error";
  hp: number; // 0-100
  onClick: (a: Agent) => void;
}

function AgentNode({ data }: NodeProps<AgentNodeData>) {
  const { agent, active, progress, status, hp, onClick } = data;
  const statusColor =
    status === "executing" ? "hsl(185 100% 55%)" :
    status === "thinking" ? "hsl(270 90% 65%)" :
    status === "done" ? "hsl(140 70% 55%)" :
    status === "error" ? "hsl(0 80% 60%)" :
    "hsl(var(--border))";

  return (
    <div
      onClick={() => onClick(agent)}
      className={cn(
        "relative rounded-2xl px-3 py-2.5 min-w-[170px] glass-strong border-2 cursor-pointer select-none",
        "transition-all duration-300 hover:scale-[1.04]",
      )}
      style={{
        borderColor: statusColor,
        boxShadow: active ? `0 0 28px ${statusColor}88` : `0 0 0 transparent`,
      }}
    >
      <Handle type="target" position={Position.Top} className="!bg-primary !w-2 !h-2 !border-0" />
      <div className="flex items-center gap-2">
        <motion.span
          animate={
            active
              ? { scale: [1, 1.15, 1], rotate: [0, 5, -5, 0] }
              : { scale: [1, 1.04, 1] }
          }
          transition={{ duration: active ? 0.8 : 3, repeat: Infinity, ease: "easeInOut" }}
          className="text-2xl"
        >
          {agent.emoji}
        </motion.span>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-display font-semibold truncate">{agent.name}</div>
          <div className="text-[9px] font-mono text-muted-foreground truncate">{agent.role}</div>
        </div>
      </div>

      {/* HP / energy bar */}
      <div className="mt-1.5 h-1.5 rounded-full bg-surface-3 overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${hp}%`,
            background: hp > 60 ? "hsl(140 70% 55%)" : hp > 30 ? "hsl(40 90% 60%)" : "hsl(0 80% 60%)",
          }}
        />
      </div>

      {/* status badge + progress */}
      <div className="mt-1 flex items-center justify-between text-[9px] font-mono">
        <span
          className="px-1 py-0.5 rounded"
          style={{ background: `${statusColor}22`, color: statusColor }}
        >
          {status}
        </span>
        <span className="text-muted-foreground">{progress}%</span>
      </div>

      {/* active progress bar */}
      {active && (
        <div className="mt-1 h-0.5 rounded-full bg-surface-3 overflow-hidden">
          <motion.div
            className="h-full"
            style={{ background: statusColor }}
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      )}

      {/* models pills */}
      <div className="mt-1.5 flex flex-wrap gap-0.5">
        {(agent.models ?? []).slice(0, 2).map((m) => (
          <span
            key={m}
            className="text-[8px] font-mono px-1 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 truncate max-w-[100px]"
          >
            {m}
          </span>
        ))}
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-primary !w-2 !h-2 !border-0" />
    </div>
  );
}

const nodeTypes = { agent: AgentNode };

const HIERARCHY: Array<{ id: string; tier: number; col: number }> = [
  { id: "orch",  tier: 0, col: 0 },
  { id: "scout", tier: 1, col: -2 },
  { id: "synth", tier: 1, col: -0.7 },
  { id: "crit",  tier: 1, col: 0.7 },
  { id: "scrb",  tier: 1, col: 2 },
  { id: "sent",  tier: 2, col: 0 },
];

const COL_W = 220;
const ROW_H = 200;

const FLOW_ORDER = ["orch", "scout", "synth", "crit", "scrb", "sent"];
const PAYLOAD_LABELS: Record<string, string> = {
  "orch->scout": "queries: 3",
  "orch->synth": "context: 12kB",
  "orch->crit":  "claims: 12",
  "orch->scrb":  "outline: 6 §",
  "scout->synth": "evidence: 24",
  "synth->crit":  "draft: 1.2k tk",
  "crit->scrb":   "verdicts: 8",
  "scrb->sent":   "final: 4.1k tk",
};

function WorkflowInner() {
  const { user } = useAuth();
  const agents = useNeural((s) => s.agents);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [name, setName] = useState("Default Workflow");
  const [running, setRunning] = useState(false);
  const [lastOutput, setLastOutput] = useState<string>("");
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const flow = useReactFlow();

  const handleAgentClick = useCallback((a: Agent) => {
    setSelectedAgent(a);
    setDrawerOpen(true);
  }, []);

  const initialNodes: Node<AgentNodeData>[] = useMemo(() => {
    return agents.map((a) => {
      const pos = HIERARCHY.find((h) => h.id === a.id) ?? { tier: 1, col: 0 };
      return {
        id: a.id,
        type: "agent",
        position: { x: 400 + pos.col * COL_W, y: 60 + pos.tier * ROW_H },
        data: {
          agent: a,
          active: false,
          progress: 0,
          status: "idle" as const,
          hp: 100,
          onClick: handleAgentClick,
        },
      };
    });
  }, [agents, handleAgentClick]);

  const initialEdges: Edge[] = useMemo(() => {
    const mkEdge = (src: string, tgt: string, color: string): Edge => {
      const key = `${src}->${tgt}`;
      return {
        id: `e_${src}_${tgt}`,
        source: src,
        target: tgt,
        animated: true,
        style: { stroke: color, strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color },
        label: PAYLOAD_LABELS[key] ?? "",
        labelStyle: { fontSize: 9, fontFamily: "ui-monospace, monospace", fill: color },
        labelBgStyle: { fill: "hsl(230 35% 8%)", fillOpacity: 0.85 },
        labelBgPadding: [4, 2] as [number, number],
        labelBgBorderRadius: 3,
        data: { latency: 0 },
      };
    };
    return [
      mkEdge("orch", "scout", "hsl(185 100% 55%)"),
      mkEdge("orch", "synth", "hsl(270 90% 65%)"),
      mkEdge("orch", "crit",  "hsl(45 95% 60%)"),
      mkEdge("orch", "scrb",  "hsl(140 70% 55%)"),
      mkEdge("scout", "synth", "hsl(185 100% 55%)"),
      mkEdge("synth", "crit",  "hsl(270 90% 65%)"),
      mkEdge("crit",  "scrb",  "hsl(45 95% 60%)"),
      mkEdge("scrb",  "sent",  "hsl(0 75% 60%)"),
    ];
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Sync nodes when initialNodes recompute (e.g. agent click handler ref)
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("workflows")
        .select("id,name")
        .eq("user_id", user.id)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) {
        setWorkflowId(data.id);
        setName(data.name);
      }
    })();
  }, [user]);

  const onConnect = useCallback(
    (c: Connection) =>
      setEdges((es) =>
        addEdge({ ...c, animated: true, style: { stroke: "hsl(185 100% 55%)", strokeWidth: 2 } }, es),
      ),
    [setEdges],
  );

  const save = async () => {
    if (!user) return toast.error("Faça login");
    const payload = {
      user_id: user.id,
      name,
      nodes: nodes as unknown as never,
      edges: edges as unknown as never,
    };
    if (workflowId) {
      const { error } = await supabase.from("workflows").update(payload).eq("id", workflowId);
      if (error) return toast.error(error.message);
      toast.success("Workflow salvo");
    } else {
      const { data, error } = await supabase.from("workflows").insert(payload).select("id").single();
      if (error) return toast.error(error.message);
      setWorkflowId(data.id);
      toast.success("Workflow criado");
    }
  };

  const setAgentState = (id: string, patch: Partial<AgentNodeData>) => {
    setNodes((ns) =>
      ns.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)),
    );
  };
  const pulseEdge = (src: string, tgt: string, latencyMs: number) => {
    setEdges((es) =>
      es.map((e) =>
        e.source === src && e.target === tgt
          ? {
              ...e,
              animated: true,
              style: { ...e.style, stroke: "hsl(185 100% 55%)", strokeWidth: 4 },
              label: `${PAYLOAD_LABELS[`${src}->${tgt}`] ?? ""} · ${latencyMs}ms`,
            }
          : e,
      ),
    );
    setTimeout(() => {
      setEdges((es) =>
        es.map((e) =>
          e.source === src && e.target === tgt
            ? { ...e, style: { ...e.style, strokeWidth: 2 } }
            : e,
        ),
      );
    }, 700);
  };

  const animateFlow = async () => {
    // Reset
    FLOW_ORDER.forEach((id) =>
      setAgentState(id, { active: false, progress: 0, status: "idle", hp: 100 }),
    );
    for (let i = 0; i < FLOW_ORDER.length; i++) {
      const id = FLOW_ORDER[i];
      setAgentState(id, { active: true, status: "thinking", progress: 10 });
      await new Promise((r) => setTimeout(r, 250));
      setAgentState(id, { status: "executing", progress: 45 });
      // pulse incoming edge
      const prev = FLOW_ORDER[i - 1];
      if (prev) {
        const latency = 80 + Math.floor(Math.random() * 220);
        pulseEdge(prev, id, latency);
      }
      await new Promise((r) => setTimeout(r, 400));
      setAgentState(id, { progress: 85, hp: Math.max(40, 100 - i * 8) });
      await new Promise((r) => setTimeout(r, 300));
      setAgentState(id, { progress: 100, status: "done", active: false });
    }
  };

  const runWorkflow = async () => {
    if (!user) return toast.error("Faça login");
    let wfId = workflowId;
    if (!wfId) {
      // create on the fly
      const { data, error } = await supabase
        .from("workflows")
        .insert({
          user_id: user.id,
          name,
          nodes: nodes as unknown as never,
          edges: edges as unknown as never,
        })
        .select("id")
        .single();
      if (error) return toast.error(error.message);
      wfId = data.id;
      setWorkflowId(wfId);
    }
    setRunning(true);
    toast.loading("Executando workflow...", { id: "wf" });
    const animPromise = animateFlow();
    try {
      const { data, error } = await supabase.functions.invoke("execute-workflow", {
        body: { workflow_id: wfId, input: "Iniciar pipeline." },
      });
      await animPromise;
      if (error) throw error;
      const out = (data as { output?: string; logs?: unknown[] }).output ?? "";
      setLastOutput(out);
      toast.success(
        `Workflow executado · ${(data as { logs?: unknown[] }).logs?.length ?? 0} nós`,
        { id: "wf" },
      );
    } catch (e) {
      await animPromise;
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg, { id: "wf" });
      // Mark last animated agent as error
      setAgentState(FLOW_ORDER[FLOW_ORDER.length - 1], { status: "error", active: false });
    } finally {
      setRunning(false);
      // Cool-down: idle after 2s
      setTimeout(() => {
        FLOW_ORDER.forEach((id) =>
          setAgentState(id, { active: false, progress: 0, status: "idle", hp: 100 }),
        );
        setEdges((es) =>
          es.map((e) => ({
            ...e,
            label: PAYLOAD_LABELS[`${e.source}->${e.target}`] ?? "",
            style: { ...e.style, strokeWidth: 2 },
          })),
        );
      }, 2200);
    }
  };

  return (
    <div className="relative h-full w-full" data-testid="workflow-mode">
      {/* Toolbar */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2 flex-wrap">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Workflow name"
          className="glass rounded-lg px-3 py-1.5 text-xs font-mono bg-transparent border border-border outline-none focus:border-primary/60 w-44"
        />
        <div className="glass rounded-lg px-3 py-1.5 text-xs font-mono">
          {nodes.length} nodes · {edges.length} edges
        </div>
        <Button size="sm" variant="outline" onClick={save} className="h-8 gap-1.5">
          <Save className="w-3 h-3" /> Salvar
        </Button>
        <Button
          size="sm"
          onClick={runWorkflow}
          disabled={running}
          className="bg-gradient-neural border-0 text-primary-foreground gap-1.5 h-8"
          data-testid="execute-workflow-btn"
        >
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          {running ? "Executando" : "Executar"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => flow.fitView({ padding: 0.2, duration: 400 })}
          className="h-8 gap-1.5"
          aria-label="Reset view"
        >
          <Maximize2 className="w-3 h-3" /> Reset
        </Button>
      </div>

      <AnimatePresence>
        {lastOutput && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute bottom-3 right-3 z-10 max-w-md glass rounded-lg p-3 text-[11px] border border-success/30"
          >
            <div className="flex items-center gap-1.5 text-success font-mono text-[10px] uppercase mb-1">
              <FileText className="w-3 h-3" /> Output do último run
            </div>
            <div className="max-h-32 overflow-y-auto whitespace-pre-wrap">
              {lastOutput.slice(0, 600)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.4}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={28} color="hsl(var(--border))" />
        <Controls className="!bg-card !border-border" />
        <MiniMap
          nodeColor={(n) => {
            const d = n.data as AgentNodeData | undefined;
            return d?.active ? "hsl(185 100% 55%)" : "hsl(230 35% 30%)";
          }}
          maskColor="hsl(230 35% 4% / 0.7)"
          className="!bg-card !border-border"
          pannable
          zoomable
        />
      </ReactFlow>

      <AgentInspectorDrawer
        agent={selectedAgent}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}

export function WorkflowMode() {
  return (
    <ReactFlowProvider>
      <WorkflowInner />
    </ReactFlowProvider>
  );
}
